import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { LoaderService } from 'src/app/shared/services/loader/loader.service';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { HorizontalCalendarComponent } from 'src/app/shared/components/vacations/horizontal-calendar/horizontal-calendar.component';
import { PetitionsComponent } from 'src/app/shared/components/vacations/petitions/petitions.component';
import { HolidaysService } from 'src/app/shared/services/holidays/holidays.service';
import {
  OperativaGroupWithAssignment,
  OperativeWeekConfigsResponse,
  OperativeWeekConfig,
  SyncProcessStartResponse,
  SyncProcessStatus,
  SyncWeekConfigsResponse,
} from 'src/app/shared/interfaces/holidays.interface';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@Component({
  selector: 'app-operational',
  imports: [
    DecimalPipe,
    LoaderComponent,
    MatIconModule,
    RouterLink,
    MatButtonModule,
    MatSlideToggleModule,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    HorizontalCalendarComponent,
    PetitionsComponent,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatProgressBarModule,
  ],
  templateUrl: './operational.component.html',
  styleUrls: [
    './operational.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class OperationalComponent implements OnInit, OnDestroy {
  operativeGroup: OperativaGroupWithAssignment | null = null;
  calendarWeeks: OperativeWeekConfig[] = [];
  operativeGroupId?: number;
  year?: number;
  id?: number;
  isLoadingCalendar: boolean = false;
  refreshingWeekNumber: number | null = null;
  private pendingRequests: number = 0;
  private isInitialLoad: boolean = false;
  reloadFlag: boolean = false;

  weekFilter = new FormControl<number | null>(null);
  weekList: { value: number; label: string }[] = [];

  // Sync progress state
  isSyncing: boolean = false;
  syncProgress: SyncProcessStatus | null = null;
  private syncPollingInterval: ReturnType<typeof setInterval> | null = null;
  private _snackBar = inject(MatSnackBar);

  constructor(
    private loaderService: LoaderService,
    private route: ActivatedRoute,
    private holidaysService: HolidaysService
  ) {}

  ngOnInit(): void {
    const operativeGroupId = this.route.snapshot.params['opId'];
    this.year = this.route.snapshot.params['year'];
    this.id = this.route.snapshot.params['id'];
    if (operativeGroupId && this.id && this.year) {
      this.operativeGroupId = Number(operativeGroupId);
      this.loaderService.showLoader();
      this.isInitialLoad = true;
      this.pendingRequests = 1; // Only one initial request - for operative group
      this.getOperativeGroup(this.operativeGroupId);
      // loadWeekConfigs will be called after group data is loaded
    }

    // Subscribe to week filter changes
    this.weekFilter.valueChanges.subscribe((weekNumber) => {
      if (weekNumber !== null) {
        this.scrollToWeek(weekNumber);
      }
    });
  }

  ngOnDestroy(): void {
    this.stopSyncPolling();
  }

  private stopSyncPolling(): void {
    if (this.syncPollingInterval) {
      clearInterval(this.syncPollingInterval);
      this.syncPollingInterval = null;
    }
  }

  private hideLoaderIfDone(): void {
    if (this.isInitialLoad && this.pendingRequests === 0) {
      this.loaderService.hideLoader();
      this.isInitialLoad = false;
    }
  }

  reloadData(): void {
    if (!this.operativeGroupId) {
      console.error('Operative Group ID is not set');
      return;
    }
    // Get the group first, which will then load week configs
    this.getOperativeGroup(this.operativeGroupId, true);
    this.reloadFlag = true;
  }

  getOperativeGroup(groupId: number, alsoLoadWeekConfigs: boolean = false) {
    if (!this.id) {
      console.error('Exercise ID is required');
      if (this.isInitialLoad) {
        this.pendingRequests--;
        this.hideLoaderIfDone();
      }
      return;
    }

    this.holidaysService.getExerciseOperativaGroup(this.id, groupId).subscribe({
      next: (response) => {
        // Extract the first group from the operativa_groups array
        if (response.operativa_groups && response.operativa_groups.length > 0) {
          this.operativeGroup = response.operativa_groups[0];
          // console.log('Operative Group data:', this.operativeGroup);

          // Load week configs after group data is available
          if (this.isInitialLoad || alsoLoadWeekConfigs) {
            if (this.isInitialLoad) {
              this.pendingRequests++; // Add one for week configs during initial load
            }
            this.loadWeekConfigs(false);
          }

          // Trigger petitions/drivers components to load data
          // This will cause the reloadEffect in petitions component to fire
          this.reloadFlag = !this.reloadFlag;
        } else {
          console.error('No operativa group found in response');
        }
        if (this.isInitialLoad) {
          this.pendingRequests--;
          this.hideLoaderIfDone();
        }
      },
      error: (error) => {
        console.error('Error fetching operative group:', error);
        if (this.isInitialLoad) {
          this.pendingRequests--;
          this.hideLoaderIfDone();
        }
      },
    });
  }

  loadWeekConfigs(isManualSync: boolean = false) {
    if (!this.id || !this.year) {
      console.error('Missing required parameters');
      if (isManualSync) {
        this.loaderService.hideLoader();
      }
      if (this.isInitialLoad) {
        this.pendingRequests--;
        this.hideLoaderIfDone();
      }
      return;
    }

    // Check if operative group is loaded
    if (!this.operativeGroup?.id) {
      console.error('Operative group must be loaded');
      this.isLoadingCalendar = false;
      if (isManualSync) {
        this.loaderService.hideLoader();
      }
      if (this.isInitialLoad) {
        this.pendingRequests--;
        this.hideLoaderIfDone();
      }
      return;
    }

    this.isLoadingCalendar = true;
    const requestData = {
      operativa_group_id: this.operativeGroup.id,
      exercise_id: this.id,
      // year: this.year,
    };

    this.holidaysService.getOperativeWeekConfigs(requestData).subscribe({
      next: (response: OperativeWeekConfigsResponse) => {
        if (response.count === 0 || response.results.length === 0) {
          // No weeks found, sync them
          this.syncWeeks(isManualSync);
        } else {
          // Directly assign the week configs
          this.calendarWeeks = response.results;
          this.populateWeekList();
          this.isLoadingCalendar = false;
          if (isManualSync) {
            this.loaderService.hideLoader();
          }
          // console.log('Week configs loaded:', this.calendarWeeks);
          if (this.isInitialLoad) {
            this.pendingRequests--;
            this.hideLoaderIfDone();
          }
        }
      },
      error: (error) => {
        this.isLoadingCalendar = false;
        if (isManualSync) {
          this.loaderService.hideLoader();
        }
        console.error('Error fetching week configs:', error);
        if (this.isInitialLoad) {
          this.pendingRequests--;
          this.hideLoaderIfDone();
        }
      },
    });
  }

  syncWeeksManually() {
    this.isSyncing = true;
    this.syncProgress = null;
    this.isLoadingCalendar = true;
    this.syncWeeks(true); // Pass flag to indicate manual sync
  }

  syncWeeks(isManualSync: boolean = false) {
    if (!this.id) {
      console.error('Missing required parameters for sync');
      this.isLoadingCalendar = false;
      this.isSyncing = false;
      if (this.isInitialLoad) {
        this.pendingRequests--;
        this.hideLoaderIfDone();
      }
      return;
    }

    // Validate that operative group is loaded
    if (!this.operativeGroup?.id) {
      console.error('Operative group must be loaded before syncing weeks');
      this.isLoadingCalendar = false;
      this.isSyncing = false;
      if (this.isInitialLoad) {
        this.pendingRequests--;
        this.hideLoaderIfDone();
      }
      return;
    }

    // isLoadingCalendar is already true from loadWeekConfigs
    const syncData = {
      operativa_group_id: this.operativeGroup.id,
      exercise_id: this.id,
    };

    this.holidaysService.syncWeekConfigs(syncData).subscribe({
      next: (response: SyncProcessStartResponse) => {
        // Start polling for status
        this.startSyncPolling(response.sync_process_id, isManualSync);
      },
      error: (error) => {
        this.isLoadingCalendar = false;
        this.isSyncing = false;
        console.error('Error starting sync:', error);
        // Check for group-related errors
        const errorMessage = error.error?.detail?.includes('no group')
          ? 'Operativa must be assigned to a group before syncing weeks'
          : error.error?.detail || 'Error al iniciar sincronización';
        this.openSnackBar(errorMessage);
        if (this.isInitialLoad) {
          this.pendingRequests--;
          this.hideLoaderIfDone();
        }
      },
    });
  }

  private startSyncPolling(processId: string, isManualSync: boolean): void {
    const INITIAL_DELAY = 5000; // 5 seconds before first ping
    const POLL_INTERVAL = 3000; // 1.5 seconds between subsequent pings

    const pollStatus = () => {
      this.holidaysService.getSyncProcessStatus(processId).subscribe({
        next: (status: SyncProcessStatus) => {
          this.syncProgress = status;

          // Check if failed
          if (status.status === 'FAILED') {
            this.stopSyncPolling();
            this.isSyncing = false;
            this.isLoadingCalendar = false;
            this.openSnackBar(
              status.error_message || `Fallaron ${status.failed_weeks} semanas`
            );
            if (this.isInitialLoad) {
              this.pendingRequests--;
              this.hideLoaderIfDone();
            }
          }
        },
        error: (error) => {
          if (error.status === 404) {
            // 404 = Success! Process was deleted after completion
            this.stopSyncPolling();
            this.isSyncing = false;
            this.syncProgress = null;
            // Reload week configs after successful sync
            this.loadWeekConfigs(isManualSync);
          } else {
            // Unexpected error
            this.stopSyncPolling();
            this.isSyncing = false;
            this.isLoadingCalendar = false;
            this.openSnackBar('Error al verificar estado de sincronización');
            console.error('Error polling sync status:', error);
            if (this.isInitialLoad) {
              this.pendingRequests--;
              this.hideLoaderIfDone();
            }
          }
        },
      });
    };

    // Wait 5 seconds before first ping, then poll every 1.5 seconds
    setTimeout(() => {
      pollStatus();
      this.syncPollingInterval = setInterval(pollStatus, POLL_INTERVAL);
    }, INITIAL_DELAY);
  }

  openSnackBar(message: string): void {
    this._snackBar.open(message, 'Cerrar');
  }

  onRefreshWeek(weekNumber: number): void {
    if (!this.id || !this.year) {
      console.error('Missing required parameters for refresh');
      return;
    }

    // Check if operative group is loaded
    if (!this.operativeGroup?.id) {
      console.error('Operative group must be loaded');
      return;
    }

    const refreshData = {
      operativa_group_id: this.operativeGroup.id,
      exercise_id: this.id,
      week_numbers: [weekNumber],
    };

    // console.log('Refreshing week number:', refreshData);

    // Show skeleton loader for this specific week
    this.refreshingWeekNumber = weekNumber;

    this.holidaysService.refreshWeekConfig(refreshData).subscribe({
      next: (response: SyncWeekConfigsResponse) => {
        // console.log('Week refreshed successfully:', response);
        // Fetch only the updated week
        this.loadSingleWeek(weekNumber);
      },
      error: (error) => {
        this.refreshingWeekNumber = null;
        console.error('Error refreshing week:', error);
      },
    });
  }

  loadSingleWeek(weekNumber: number): void {
    if (!this.id || !this.year) {
      console.error('Missing required parameters');
      this.refreshingWeekNumber = null;
      return;
    }

    // Check if operative group is loaded
    if (!this.operativeGroup?.id) {
      console.error('Operative group must be loaded');
      this.refreshingWeekNumber = null;
      return;
    }

    const requestData = {
      operativa_group_id: this.operativeGroup.id,
      exercise_id: this.id,
      // year: this.year,
      week_numbers: [weekNumber],
    };

    this.holidaysService.getOperativeWeekConfigs(requestData).subscribe({
      next: (response: OperativeWeekConfigsResponse) => {
        if (response.results.length > 0) {
          // Get the updated week directly
          const updatedWeek = response.results[0];

          // Find and replace the week in the array
          const weekIndex = this.calendarWeeks.findIndex(
            (week) => week.week_number === weekNumber
          );

          if (weekIndex !== -1) {
            // Create a new array with the updated week to trigger change detection
            this.calendarWeeks = [
              ...this.calendarWeeks.slice(0, weekIndex),
              updatedWeek,
              ...this.calendarWeeks.slice(weekIndex + 1),
            ];
            // console.log('Week updated successfully:', updatedWeek);
          }
        }
        this.refreshingWeekNumber = null;
      },
      error: (error) => {
        this.refreshingWeekNumber = null;
        console.error('Error fetching updated week config:', error);
      },
    });
  }

  onWeekUpdated(): void {
    // Reload week configs after a successful update
    this.loadWeekConfigs();
  }

  populateWeekList(): void {
    this.weekList = this.calendarWeeks.map((week) => ({
      value: week.week_number,
      label: `Semana ${week.week_number}`,
    }));
  }

  scrollToWeek(weekNumber: number): void {
    // Find the index of the week in the calendar
    const weekIndex = this.calendarWeeks.findIndex(
      (week) => week.week_number === weekNumber
    );

    if (weekIndex !== -1) {
      // Use setTimeout to ensure the DOM is ready
      setTimeout(() => {
        const scrollContainer = document.querySelector('.right-columns-scroll');
        const weekColumn =
          document.querySelectorAll('.right-column')[weekIndex];

        if (scrollContainer && weekColumn) {
          weekColumn.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start',
          });
        }
      }, 100);
    }
  }

  get isLoading(): boolean {
    return this.loaderService.getLoader();
  }

  onExportLoading(isLoading: boolean): void {
    if (isLoading) {
      this.loaderService.showLoader();
    } else {
      this.loaderService.hideLoader();
    }
  }
}
